import React, { useEffect, useRef, useState } from 'react';
import './manage-store-page.styles.scss';
import { faPlus, faTag } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import StoreButtonRow from '@/components/PLAYER/store/store-components/store-buttons.component';
import { useSelector } from 'react-redux';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import ManageTradePortal from './manage-trade-portal.component';
import ItemSearchBar from '@/components/SHARED/search-bar/item-search-bar.component';
import StoreItemCard from '@/components/PLAYER/store/store-components/store-item-card.component';
import ManageItemList from './manage-item-list.component';
import { getGameElementsFromDb } from '@/utils/getDataFromDb';
import { get } from 'firebase/database';
import useAnalytics from '@/utils/useAnalytics';

const ManageStorePage = ({ 
    gameData, 
    gameElements, 
    gameStats 
}) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const membership = useSelector(state => state.userData.membership);
    const [ allItems, setAllItems ] = useState(null);
    const [ filteredItems, setFilteredItems ] = useState([]);
    const [ itemsToDisplay, setItemsToDisplay ] = useState([]);
    const [ itemType, setItemType ] = useState('inventory');
    const [ collections, setCollections ] = useState([]);
    const [ status, setStatus ] = useState(null);
    const [ saleList, setSaleList ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const { trackEvent, trackException } = useAnalytics();
    const approveRef = useRef();
    const itemsRef = useRef();
    const availableRef = useRef();
    const navigate = useNavigate();
    let itemsListener;

    useEffect(() => {

        return () => {
            if (itemsListener) itemsListener();
        }
    }, [])

    useEffect(() => {
        if (!gameData) return;
        getStoreElements();
        getCollections();
        getSalesList();
    }, [gameData])

    useEffect(() => {
        if (!allItems || !itemType) return;
        console.log(itemType);
        let items = allItems.filter(i => 
            i.opt_itemType === itemType &&
            i.opt_market
        );
        setFilteredItems([...items]);
        setItemsToDisplay([...items]);
    },[allItems, itemType])

    function getStoreElements() {
        const now = new Date().getTime();
        getGameElementsFromDb({
            "gameId": gameData.gameId,
            "market": true,
            "callback": callback,
            "handleListener": handleListener
        });
        function callback(data) {
            console.log(data);
            let tempData = {...itemsRef.current};
            if (
                data.status === 'deleted'
            ) {
                delete tempData[data.id];
            } else {
                itemsRef.current = {
                    ...tempData,
                    ...{[data.id]: data}
                }
            }
            // console.log(itemsRef.current);
            setAllItems(Object.values(itemsRef.current));
            let statusObj = {}
            for (let i of Object.values(itemsRef.current)) {
                if (
                    i.opt_visible &&
                    (!i.opt_dateOpen || i.opt_dateOpen < now) &&
                    (!i.opt_dateClose || i.opt_dateClose > now)
                ) {
                    statusObj[i.id] = true;
                } else if (statusObj[i.id]) {
                    delete statusObj[i.id];
                }
            }
            setStatus({...statusObj})
        }
        function handleListener(unsub) {
            itemsListener = unsub;
        }
    }

    function getSalesList() {
        getGameElementsFromDb({
            "gameId": gameData.gameId,
            "elementType": 'sale',
            "callback": callback,
            "handleListener": handleListener
        });
        function callback(data) {
            if (data.status === 'deleted') {
                setSaleList(prev => prev.filter(s => s.id !== data.id));
            } else {
                setSaleList(prev => {
                    let temp = [...prev];
                    let index = temp.findIndex(s => s.id === data.id);
                    if (index > -1) {
                        temp[index] = data;
                    } else {
                        temp.push(data);
                    }
                    return temp;
                });
            }
        }
        function handleListener(unsub) {
            itemsListener = unsub;
        }

    }

    function getCollections() {
        getGameElementsFromDb({
            "gameId": gameData.gameId,
            "elementType": 'collection',
            "callback": callback,
            "handleListener": handleListener
        });
        function callback(data) {
            if (data.status === 'deleted') {
                setCollections(prev => prev.filter(c => c.id !== data.id));
            } else {
                setCollections(prev => {
                    let temp = [...prev];
                    let index = temp.findIndex(c => c.id === data.id);
                    if (index > -1) {
                        temp[index] = data;
                    } else {
                        temp.push(data);
                    }
                    return temp;
                });
            }
        }
        function handleListener(unsub) {
            itemsListener = unsub;
        }
    }

    function removeItemFromStore(item) {
        setConfirmMessage(`Are you sure you want to remove this item from the store? ${
            item.opt_itemType !== 'contribution' ? 
            'This will not remove the price you set for this item, and you can put the item back in the store by clicking "Add Items".' :
            ''
        }`)
        approveRef.current = () => {
            continueRemoval()
        }

        async function continueRemoval() {
            setLoading(item.id);
            console.log(item);
            item.opt_market = false;
            const res = await UseCloudFunction(
                'changeStoreStatus',
                {
                    'item': item,
                    'gameId': gameData.gameId,
                    'appId': userProfile.appId,
                }
            );
            console.log(res);
            if (res.error) {
                setAlertMessage(`Something went wrong: ${res.error}`);
                setLoading(null);
                trackException(`Error removing item from store: ${res.error}`);
                return;
            }
            let tempData = {...itemsRef.current};
            delete tempData[item.id];
            
            // console.log(itemsRef.current);
            setAllItems(Object.values({...tempData}));
            setLoading(null);
            trackEvent(
                'item_removed_from_store',
                'Game Manager',
                `Item ${item.id} removed from store for game ${gameData.gameId}.`,
                null,
                membership
            );
            setConfirmMessage(null);
        }
    }

    function search(text) {
        if (!text) {
            setItemsToDisplay([...availableRef.current])
            return;
        }
        let tempItems = [...availableRef.current]
        let filteredItems = tempItems
        .filter(i => 
            i.name.toLowerCase().includes(text.toLowerCase()) ||
            i.desc.toLowerCase().includes(text.toLowerCase())
        )
        setItemsToDisplay([...filteredItems]);
    }

    return (
        <div className={`manage-store-page ${screenSize}`}>
            <div className='manage-store-buttons'>
                <button 
                    className='g-button med-btn'
                    onClick={() => navigate('additems')}
                >
                    Add Items
                    <FontAwesomeIcon icon={faPlus} />
                </button>
                {/* {
                    (saleList.length > 0)
                    ?
                    <button 
                        className='g-button med-btn disabled'
                        onClick={() => navigate('sales')}
                    >
                        Manage Sales
                        <FontAwesomeIcon icon={faTag} />
                    </button>
                    :
                    <button 
                        className='g-button med-btn disabled'
                        onClick={() => navigate('sales/create')}
                    >
                        Create Sale
                        <FontAwesomeIcon icon={faTag} />
                    </button>
                } */}
                
                <StoreButtonRow
                    gameData={gameData} 
                    allItems={allItems} 
                    setItemType={setItemType} 
                    itemType={itemType}
                    isManager={true} />
            </div>
            <div className='manage-store-content'>
                <ItemSearchBar 
                    allItems={filteredItems}
                    setItemsToDisplay={setItemsToDisplay}
                    collections={collections}
                    itemType={itemType} 
                    noTypes={true}
                />
                <div className="g-space-1"></div>
                {
                    (itemType === 'tradeportal')
                    ?
                    <ManageTradePortal />
                    :
                    (itemsToDisplay.length > 0)
                    ?
                    <ManageItemList
                        itemsToDisplay={itemsToDisplay} 
                        gameElements={gameElements}
                        gameStats={gameStats}
                        saleList={saleList}
                        gameData={gameData}
                        removeItemFromStore={removeItemFromStore}
                    />
                    :
                    <h4>No Items</h4>
                }
            </div>
            <ModalAlert
                show={!!alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
            <ModalConfirm
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current}
            />
        </div>
    )
}

export default ManageStorePage